import * as React from "react";
import { Box, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import {
  buildGamePointAssignments,
  GamePointAssignmentExample,
  TriagePlayerId,
  TRIAGE_PLAYER_IDS,
} from "./buildGamePointAssignments";
import type {
  PlayerIndexMap,
  RallyBadge,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { FirefightClip } from "./GameStory_firefights";
import type { PounceOverlay } from "./GameStory_pounceWindows";
import type { KitchenArrivalRow } from "./RallyBoardPopup";
import type { CvJson } from "../../shared/types/cvJson";
import type { PounceWindowRow } from "../../shared/types/pounceEtc";

type PlayerLensKey =
  | "fixMistakes"
  | "findOpportunities"
  | "understandPatterns"
  | "whatsWorking";

type PlayerLensEvidence = {
  id: string;
  rallyIndex: number;
  shotNumber: number | null;
  playbackStartShotNumber?: number | null;
  loopToRallyEnd?: boolean;
  title: string;
  detail: string;
  source: "assignment" | "pounce";
  pounceWindowRow?: PounceWindowRow;
};

type PlayerLensClaim = {
  id: string;
  lens: PlayerLensKey;
  playerId: TriagePlayerId;
  title: string;
  answer: string;
  why: string;
  evidence: PlayerLensEvidence[];
  score: number;
};

const MIN_TAKEN_POUNCE_MOVEMENT_FEET = 2;

const PLAYER_LENSES: Array<{
  key: PlayerLensKey;
  label: string;
  question: string;
}> = [
  {
    key: "whatsWorking",
    label: "What's Working",
    question: "What should I keep doing?",
  },
  {
    key: "findOpportunities",
    label: "Find Opportunities",
    question: "Where could I have been more ready?",
  },
  {
    key: "fixMistakes",
    label: "Fix Mistakes",
    question: "What mistakes are you talking about?",
  },
  {
    key: "understandPatterns",
    label: "Understand Patterns",
    question: "What kept showing up?",
  },
];

function teamOfPlayerId(playerId: number): 0 | 1 {
  return playerId < 2 ? 0 : 1;
}

function scoreText(score: [number, number] | null | undefined): string {
  if (!score) return "?";
  return `${score[0]}-${score[1]}`;
}

function lensColor(lens: PlayerLensKey): string {
  switch (lens) {
    case "fixMistakes":
      return "rgba(185,85,45,0.96)";
    case "findOpportunities":
      return "rgba(190,130,25,0.98)";
    case "understandPatterns":
      return "rgba(105,85,180,0.96)";
    case "whatsWorking":
      return "rgba(35,140,75,0.96)";
    default:
      return "rgba(0,0,0,0.7)";
  }
}

function lensBg(lens: PlayerLensKey): string {
  switch (lens) {
    case "fixMistakes":
      return "rgba(185,85,45,0.10)";
    case "findOpportunities":
      return "rgba(190,130,25,0.12)";
    case "understandPatterns":
      return "rgba(105,85,180,0.10)";
    case "whatsWorking":
      return "rgba(35,140,75,0.10)";
    default:
      return "rgba(0,0,0,0.05)";
  }
}

function makeAssignmentEvidence(
  ex: GamePointAssignmentExample,
  preferredShotNumber?: number | null,
  playbackOptions?: Pick<
    PlayerLensEvidence,
    "playbackStartShotNumber" | "loopToRallyEnd"
  >,
): PlayerLensEvidence {
  const shotNumber = preferredShotNumber ?? ex.focusShotNumber;
  const shotText =
    shotNumber != null ? ` shot ${shotNumber}` : " rally";
  return {
    id: `assignment-${ex.bucketKey}-${ex.rallyIndex}-${shotNumber ?? "r"}`,
    rallyIndex: ex.rallyIndex,
    shotNumber,
    ...playbackOptions,
    title: `Rally ${ex.rallyIndex + 1}${shotNumber ? ` • Shot ${shotNumber}` : ""}`,
    detail: `${ex.reasonLabel} at ${scoreText(ex.scoreBefore)}${shotText}`,
    source: "assignment",
  };
}

function makePounceEvidence(row: PounceWindowRow): PlayerLensEvidence {
  const shotNumber = row.shotIndex + 1;
  return {
    id: `pounce-${row.rallyIndex}-${row.shotIndex}-${row.focusedPlayerId}`,
    rallyIndex: row.rallyIndex,
    shotNumber,
    title: `Rally ${row.rallyIndex + 1} • Shot ${shotNumber}`,
    detail: `${row.summaryLabel || "Attack chance not taken"} near ${row.zoneAtOpponentImpact}`,
    source: "pounce",
    pounceWindowRow: row,
  };
}

function cleanReasonLabel(label: string): string {
  return label.replace(/\s+/g, " ").trim() || "assigned moments";
}

function sortEvidence(a: PlayerLensEvidence, b: PlayerLensEvidence): number {
  if (a.rallyIndex !== b.rallyIndex) return a.rallyIndex - b.rallyIndex;
  return (a.shotNumber ?? 999) - (b.shotNumber ?? 999);
}

function pushClaim(claims: PlayerLensClaim[], claim: PlayerLensClaim) {
  if (claim.evidence.length === 0) return;
  claims.push({
    ...claim,
    evidence: claim.evidence.slice().sort(sortEvidence),
  });
}

function getOverlayStartMsAtOrBefore(
  row: KitchenArrivalRow | null,
  shotNumber: number | null,
): number | null {
  if (!row || shotNumber == null) return null;

  const overlay = (row.shotNumberOverlays ?? [])
    .filter(
      (s) =>
        typeof s.shotNumber === "number" &&
        s.shotNumber <= shotNumber &&
        typeof s.atMsRel === "number" &&
        Number.isFinite(s.atMsRel),
    )
    .sort((a, b) => b.shotNumber - a.shotNumber)[0];

  if (!overlay) return null;
  const segmentStartMs = row.segmentStartMs ?? row.startMs ?? 0;
  return segmentStartMs + overlay.atMsRel;
}

function getLastTurningPointShotNumber(row: KitchenArrivalRow | null): number | null {
  if (!row) return null;
  const candidates = Array.isArray(row.breakShotNumbers)
    ? row.breakShotNumbers.filter(
        (n) => typeof n === "number" && Number.isFinite(n),
      )
    : [];
  if (candidates.length) return Math.max(...candidates);
  return typeof row.breakShotNumber === "number" ? row.breakShotNumber : null;
}

function getPounceLoopRegionMs(
  row: KitchenArrivalRow | null,
  pounce: PounceWindowRow | null | undefined,
): { startMs: number; endMs: number } | null {
  if (!row || !pounce) return null;

  const starts = [
    pounce.opponentContact?.ms,
    pounce.attackBand?.entryMs,
    pounce.attackBand?.firstAttackablePoint?.ms,
    pounce.attackBand?.bestAttackPoint?.ms,
  ].filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  const ends = [
    pounce.attackBand?.exitMs,
    pounce.attackBand?.bestAttackPoint?.ms,
    pounce.attackBand?.firstAttackablePoint?.ms,
    pounce.opponentContact?.ms,
  ].filter((v): v is number => typeof v === "number" && Number.isFinite(v));

  if (!starts.length || !ends.length) return null;

  const startMs = Math.max(row.startMs, Math.min(...starts) - 1000);
  const endMs = Math.min(row.endMs, Math.max(...ends) + 1500);
  if (endMs <= startMs) return null;
  return { startMs, endMs };
}

function getEvidenceLoopRegionMs(
  row: KitchenArrivalRow | null,
  evidence: PlayerLensEvidence | null,
): { startMs: number; endMs: number } | null {
  if (!row || !evidence) return null;

  const pounceLoop = getPounceLoopRegionMs(row, evidence.pounceWindowRow);
  if (pounceLoop) return pounceLoop;

  if (!evidence.loopToRallyEnd) return null;

  const startShotNumber =
    evidence.playbackStartShotNumber ?? evidence.shotNumber;
  const shotStartMs = getOverlayStartMsAtOrBefore(row, startShotNumber ?? null);
  if (shotStartMs == null) return null;

  const startMs = Math.max(row.startMs, shotStartMs - 250);
  const endMs = Math.min(row.segmentEndMs ?? row.endMs, row.endMs);
  if (endMs <= startMs) return null;
  return { startMs, endMs };
}

function getEvidenceSeedPlayback(args: {
  row: KitchenArrivalRow | null;
  evidence: PlayerLensEvidence | null;
  nonce: number;
}): { timeSec: number; paused: boolean; nonce: number } | undefined {
  const { row, evidence, nonce } = args;
  if (!row || !evidence?.shotNumber) return undefined;

  const loop = getEvidenceLoopRegionMs(row, evidence);
  const seedShotNumber =
    evidence.playbackStartShotNumber ?? evidence.shotNumber;
  const shotStartMs = getOverlayStartMsAtOrBefore(row, seedShotNumber);
  const startMs =
    loop?.startMs ??
    (shotStartMs != null ? Math.max(row.startMs, shotStartMs - 1500) : null);

  if (startMs == null) return undefined;
  const segmentStartMs = row.segmentStartMs ?? row.startMs ?? 0;
  return {
    timeSec: Math.max(0, (startMs - segmentStartMs) / 1000),
    paused: false,
    nonce,
  };
}

function buildPounceOverlay(
  row: KitchenArrivalRow | null,
  pounce: PounceWindowRow | null | undefined,
): PounceOverlay | null {
  if (!row || !pounce) return null;

  const overlayStartMs = Math.max(
    row.startMs,
    (pounce.attackBand.entryMs ??
      pounce.attackBand.firstAttackablePoint?.ms ??
      pounce.opponentContact.ms ??
      row.startMs) - 700,
  );
  const overlayEndMs = Math.min(
    row.endMs,
    (pounce.attackBand.exitMs ??
      pounce.attackBand.bestAttackPoint?.ms ??
      pounce.attackBand.firstAttackablePoint?.ms ??
      pounce.opponentContact.ms ??
      row.endMs) + 1000,
  );

  const bestAttack =
    pounce.attackBand.bestAttackPoint ?? pounce.attackBand.firstAttackablePoint;

  return {
    enabled: true,
    focusedPlayerId: pounce.focusedPlayerId,
    opponentPlayerId: pounce.opponentPlayerId,
    triggerShotNumber: pounce.shotIndex + 1,
    overlayStartMs,
    overlayEndMs,
    entryMs: pounce.attackBand.entryMs,
    exitMs: pounce.attackBand.exitMs,
    pounceResult: pounce.pounceResult,
    opponentContact: pounce.opponentContact,
    playerAtOpponentImpact: pounce.playerAtOpponentImpact,
    firstAttackablePoint: pounce.attackBand.firstAttackablePoint,
    bestAttackPoint: pounce.attackBand.bestAttackPoint,
    movementDistanceFeet: pounce.movementToBestAttackPoint.distanceFeet,
    movementDirection: pounce.movementToBestAttackPoint.direction,
    peakPoint: {
      x: pounce.ballGeometry.peakX,
      y: pounce.ballGeometry.peakY,
      z: pounce.ballGeometry.peakZ,
    },
    peakHeightOverNet: pounce.ballGeometry.peakHeightOverNet,
    requiredMovement: {
      enabled: true,
      startPoint:
        pounce.playerAtOpponentImpact.x != null &&
        pounce.playerAtOpponentImpact.y != null
          ? {
              x: pounce.playerAtOpponentImpact.x,
              y: pounce.playerAtOpponentImpact.y,
            }
          : null,
      endPoint:
        bestAttack?.x != null && bestAttack?.y != null
          ? { x: bestAttack.x, y: bestAttack.y }
          : null,
      firstAttackablePoint:
        pounce.attackBand.firstAttackablePoint?.x != null &&
        pounce.attackBand.firstAttackablePoint?.y != null
          ? {
              x: pounce.attackBand.firstAttackablePoint.x,
              y: pounce.attackBand.firstAttackablePoint.y,
            }
          : null,
      bestAttackPoint:
        pounce.attackBand.bestAttackPoint?.x != null &&
        pounce.attackBand.bestAttackPoint?.y != null
          ? {
              x: pounce.attackBand.bestAttackPoint.x,
              y: pounce.attackBand.bestAttackPoint.y,
            }
          : null,
      distanceFeet: pounce.movementToBestAttackPoint.distanceFeet,
      dxFeet: pounce.movementToBestAttackPoint.dxFeet,
      dyFeet: pounce.movementToBestAttackPoint.dyFeet,
      direction: pounce.movementToBestAttackPoint.direction,
    },
    pounceShotNumber:
      pounce.pounceResult === "taken" ? pounce.shotIndex + 2 : null,
    pounceShotStartMs: pounce.pounceShotTrajectory?.start?.ms ?? null,
    pounceShotEndMs: pounce.pounceShotTrajectory?.end?.ms ?? null,
    pounceShotTrajectory: {
      start: pounce.pounceShotTrajectory?.start ?? null,
      peak: pounce.pounceShotTrajectory?.peak ?? null,
      end: pounce.pounceShotTrajectory?.end ?? null,
    },
  };
}

function buildReasonClaims(args: {
  playerId: TriagePlayerId;
  lens: PlayerLensKey;
  examples: GamePointAssignmentExample[];
  titlePrefix: string;
  answerForReason: (reasonLabel: string, count: number) => string;
  why: string;
  makeEvidence?: (ex: GamePointAssignmentExample) => PlayerLensEvidence;
}): PlayerLensClaim[] {
  const byReason: Record<string, GamePointAssignmentExample[]> = {};

  for (const ex of args.examples) {
    const key = cleanReasonLabel(ex.reasonLabel);
    byReason[key] = byReason[key] ?? [];
    byReason[key].push(ex);
  }

  return Object.keys(byReason)
    .map((reasonLabel) => {
      const examples = byReason[reasonLabel];
      return {
        id: `${args.lens}-${args.playerId}-${reasonLabel}`,
        lens: args.lens,
        playerId: args.playerId,
        title: `${args.titlePrefix}: ${reasonLabel}`,
        answer: args.answerForReason(reasonLabel, examples.length),
        why: args.why,
        evidence: examples.map((ex) =>
          args.makeEvidence ? args.makeEvidence(ex) : makeAssignmentEvidence(ex),
        ),
        score: examples.length * 100,
      };
    })
    .sort((a, b) => b.score - a.score);
}

function buildPlayerLensClaims(args: {
  playerId: TriagePlayerId;
  rows: KitchenArrivalRow[];
  badgesByRallyIndex?: Map<number, RallyBadge[]>;
  pounceWindowRows?: PounceWindowRow[];
}): PlayerLensClaim[] {
  const assignments = buildGamePointAssignments({
    rows: args.rows,
    badgesByRallyIndex: args.badgesByRallyIndex,
    pounceWindowRows: args.pounceWindowRows,
  });
  const buckets = assignments.byPlayerId[args.playerId];
  const claims: PlayerLensClaim[] = [];
  const rowByRallyIndex = new Map(args.rows.map((row) => [row.rallyIndex, row]));

  const makeWorkingEvidence = (ex: GamePointAssignmentExample) => {
    const row = rowByRallyIndex.get(ex.rallyIndex);
    const lastTurningPointShotNumber = getLastTurningPointShotNumber(row ?? null);
    const useBreakShot =
      cleanReasonLabel(ex.reasonLabel).toLowerCase() ===
        "setup shot mattered most" &&
      typeof lastTurningPointShotNumber === "number";
    return makeAssignmentEvidence(
      ex,
      useBreakShot ? lastTurningPointShotNumber : undefined,
      useBreakShot
        ? {
            playbackStartShotNumber:
              typeof lastTurningPointShotNumber === "number"
                ? Math.max(1, lastTurningPointShotNumber - 1)
                : null,
            loopToRallyEnd: true,
          }
        : undefined,
    );
  };

  const lostExamples = [
    ...(buckets.gamePointsLost?.examples ?? []),
    ...(buckets.opportunitiesLost?.examples ?? []),
  ].filter((ex) => ex.assignmentKind === "blame");

  for (const claim of buildReasonClaims({
    playerId: args.playerId,
    lens: "fixMistakes",
    examples: lostExamples,
    titlePrefix: "Fix",
    answerForReason: (reasonLabel, count) =>
      `${count} clip${count === 1 ? "" : "s"} where the deterministic attribution says this player was connected to ${reasonLabel.toLowerCase()}.`,
    why: "Start here when you want the concrete mistakes behind lost scoring chances.",
  })) {
    pushClaim(claims, claim);
  }

  const opportunityExamples = buckets.opportunitiesLost?.examples ?? [];
  pushClaim(claims, {
    id: `findOpportunities-${args.playerId}-serve-chances`,
    lens: "findOpportunities",
    playerId: args.playerId,
    title: "Missed serve chances",
    answer: `${opportunityExamples.length} clip${opportunityExamples.length === 1 ? "" : "s"} where this player's team had the serve and did not convert the chance.`,
    why: "These are not necessarily bad shots by themselves; they are places to ask what would have kept the scoring chance alive.",
    evidence: opportunityExamples.map((ex) => makeAssignmentEvidence(ex)),
    score: opportunityExamples.length * 100,
  });

  const pounceMisses = (args.pounceWindowRows ?? []).filter(
    (row) =>
      row.focusedPlayerId === args.playerId &&
      row.pounceResult === "not_taken",
  );
  pushClaim(claims, {
    id: `findOpportunities-${args.playerId}-pounce`,
    lens: "findOpportunities",
    playerId: args.playerId,
    title: "Pounce readiness checks",
    answer: `${pounceMisses.length} clip${pounceMisses.length === 1 ? "" : "s"} where the data saw a possible attack window or poach-like moment, but the pounce was not taken.`,
    why: "Use these as prompts, not verdicts. The useful question is whether this player could have been loaded, leaning, or positioned a half-step earlier.",
    evidence: pounceMisses.map(makePounceEvidence),
    score: 10000 + pounceMisses.length * 80,
  });

  const repeatedReasonClaims = buildReasonClaims({
    playerId: args.playerId,
    lens: "understandPatterns",
    examples: assignments.allExamples.filter(
      (ex) => ex.assignedPlayerId === args.playerId,
    ),
    titlePrefix: "Repeated attribution",
    answerForReason: (reasonLabel, count) =>
      `${count} assigned clip${count === 1 ? "" : "s"} share the same label: ${reasonLabel}.`,
    why: "This lens is for seeing whether the same kind of rally keeps coming back, rather than deciding whether one clip is right or wrong.",
  }).filter((claim) => claim.evidence.length >= 2);
  for (const claim of repeatedReasonClaims) pushClaim(claims, claim);

  const workingExamples = [
    ...(buckets.gamePointsWon?.examples ?? []),
    ...(buckets.opponentOpportunitiesStymied?.examples ?? []),
  ].filter((ex) => ex.assignmentKind === "credit");

  const takenPounces = (args.pounceWindowRows ?? []).filter((row) => {
    const distance = row.movementToBestAttackPoint.distanceFeet;
    return (
      row.focusedPlayerId === args.playerId &&
      row.pounceResult === "taken" &&
      typeof distance === "number" &&
      distance >= MIN_TAKEN_POUNCE_MOVEMENT_FEET
    );
  });
  pushClaim(claims, {
    id: `whatsWorking-${args.playerId}-pounce-taken`,
    lens: "whatsWorking",
    playerId: args.playerId,
    title: "Pounce taken",
    answer: `${takenPounces.length} clip${takenPounces.length === 1 ? "" : "s"} where this player moved at least ${MIN_TAKEN_POUNCE_MOVEMENT_FEET} ft and took the pounce window.`,
    why: "These are good readiness examples: the player saw the window, moved enough for it to matter, and acted on it.",
    evidence: takenPounces.map(makePounceEvidence),
    score: 9000 + takenPounces.length * 80,
  });

  for (const claim of buildReasonClaims({
    playerId: args.playerId,
    lens: "whatsWorking",
    examples: workingExamples,
    titlePrefix: "Keep",
    answerForReason: (reasonLabel, count) =>
      `${count} clip${count === 1 ? "" : "s"} where this player received credit connected to ${reasonLabel.toLowerCase()}.`,
    why: "These are the clips to preserve: look for the setup, contact height, pressure, or court position that made the point go your way.",
    makeEvidence: makeWorkingEvidence,
  })) {
    pushClaim(claims, claim);
  }

  return claims.sort((a, b) => b.score - a.score);
}

export function PlayerLensReviewPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  rows: KitchenArrivalRow[];
  cvJson?: CvJson | null;
  renderPlayerIdentity: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips?: FirefightClip[];
  firefightRallySet?: Set<number>;
  badgesByRallyIndex?: Map<number, RallyBadge[]>;
  pounceWindowRows?: PounceWindowRow[];
}) {
  const {
    open,
    onClose,
    packageId,
    rows,
    cvJson,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    firefightsClips = [],
    firefightRallySet = new Set<number>(),
    badgesByRallyIndex,
    pounceWindowRows,
  } = props;

  const [selectedPlayerId, setSelectedPlayerId] =
    React.useState<TriagePlayerId>(0);
  const [activeLens, setActiveLens] =
    React.useState<PlayerLensKey>("whatsWorking");
  const [selectedClaimIndex, setSelectedClaimIndex] = React.useState(0);
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = React.useState(0);

  const allClaims = React.useMemo(
    () =>
      buildPlayerLensClaims({
        playerId: selectedPlayerId,
        rows,
        badgesByRallyIndex,
        pounceWindowRows,
      }),
    [selectedPlayerId, rows, badgesByRallyIndex, pounceWindowRows],
  );

  const lensClaims = React.useMemo(
    () => allClaims.filter((claim) => claim.lens === activeLens),
    [allClaims, activeLens],
  );

  React.useEffect(() => {
    setSelectedClaimIndex(0);
    setSelectedEvidenceIndex(0);
  }, [selectedPlayerId, activeLens]);

  const selectedClaim = lensClaims[selectedClaimIndex] ?? null;
  const selectedEvidence =
    selectedClaim?.evidence[selectedEvidenceIndex] ??
    selectedClaim?.evidence[0] ??
    null;
  const selectedRow =
    selectedEvidence != null ? rows[selectedEvidence.rallyIndex] ?? null : null;
  const selectedPounceOverlay = React.useMemo(
    () => buildPounceOverlay(selectedRow, selectedEvidence?.pounceWindowRow),
    [selectedRow, selectedEvidence],
  );
  const selectedLoopRegion = React.useMemo(() => {
    const loop = getEvidenceLoopRegionMs(selectedRow, selectedEvidence);
    if (!loop || !selectedRow || !selectedEvidence) return undefined;
    return {
      enabled: true,
      aSec: (loop.startMs - selectedRow.segmentStartMs) / 1000,
      bSec: (loop.endMs - selectedRow.segmentStartMs) / 1000,
      nonce: selectedEvidenceIndex + selectedEvidence.rallyIndex * 100,
    };
  }, [selectedRow, selectedEvidence, selectedEvidenceIndex]);
  const selectedSeedPlayback = React.useMemo(
    () =>
      getEvidenceSeedPlayback({
        row: selectedRow,
        evidence: selectedEvidence,
        nonce:
          selectedEvidenceIndex +
          (selectedEvidence?.rallyIndex ?? 0) * 100 +
          (selectedEvidence?.shotNumber ?? 0) * 10000,
      }),
    [selectedRow, selectedEvidence, selectedEvidenceIndex],
  );
  const activeLensInfo =
    PLAYER_LENSES.find((lens) => lens.key === activeLens) ?? PLAYER_LENSES[0];

  if (!open) return null;

  return (
    <Box
      data-happy-path="player-lens-popup"
      data-selected-player-id={selectedPlayerId}
      data-active-lens={activeLens}
      sx={{
        position: "fixed",
        inset: 14,
        zIndex: 2200,
        bgcolor: "white",
        color: "rgba(0,0,0,0.86)",
        border: "1px solid rgba(0,0,0,0.22)",
        borderRadius: 1,
        boxShadow: "0 18px 70px rgba(0,0,0,0.36)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.35,
          py: 0.85,
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box sx={{ fontSize: 18, fontWeight: 900 }}>Player Lens Review</Box>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 1.1,
          py: 0.75,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          display: "flex",
          gap: 0.75,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ fontSize: 12.5, fontWeight: 900, mr: 0.2 }}>Player</Box>
        {TRIAGE_PLAYER_IDS.map((playerId) => {
          const selected = selectedPlayerId === playerId;
          const playerTeam = teamOfPlayerId(playerId);
          return (
            <Button
              key={playerId}
              data-happy-path={`player-lens-player-${playerId}`}
              data-selected={selected ? "true" : "false"}
              size="small"
              variant={selected ? "contained" : "outlined"}
              onClick={() => setSelectedPlayerId(playerId)}
              sx={{
                textTransform: "none",
                minWidth: 78,
                px: 0.7,
                py: 0.35,
                bgcolor: selected
                  ? playerTeam === 0
                    ? "rgba(60,145,235,0.92)"
                    : "rgba(45,165,90,0.92)"
                  : "transparent",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.55 }}>
                <Box sx={{ fontWeight: 900 }}>p{playerId}</Box>
                {renderPlayerIdentity({
                  playerId,
                  size: 34,
                  fontSize: 10,
                  noLabel: true,
                })}
              </Box>
            </Button>
          );
        })}

        <Box sx={{ width: 14 }} />
        <Box sx={{ fontSize: 12.5, fontWeight: 900, mr: 0.2 }}>Lens</Box>
        {PLAYER_LENSES.map((lens) => {
          const selected = activeLens === lens.key;
          return (
            <Button
              key={lens.key}
              data-happy-path={`player-lens-lens-${lens.key}`}
              data-selected={selected ? "true" : "false"}
              size="small"
              variant={selected ? "contained" : "outlined"}
              onClick={() => setActiveLens(lens.key)}
              sx={{
                textTransform: "none",
                minHeight: 30,
                fontWeight: 850,
                bgcolor: selected ? lensColor(lens.key) : "transparent",
                borderColor: lensColor(lens.key),
                color: selected ? "white" : lensColor(lens.key),
                "&:hover": {
                  bgcolor: selected ? lensColor(lens.key) : lensBg(lens.key),
                  borderColor: lensColor(lens.key),
                },
              }}
            >
              {lens.label}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box
          sx={{
            width: 430,
            minWidth: 430,
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflowY: "auto",
            p: 1,
          }}
        >
          <Box
            sx={{
              fontSize: 20,
              fontWeight: 950,
              color: lensColor(activeLens),
              mb: 0.85,
              lineHeight: 1.1,
            }}
          >
            {activeLensInfo.label}
          </Box>

          {lensClaims.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {lensClaims.map((claim, idx) => {
                const selected = idx === selectedClaimIndex;
                return (
                  <Box
                    key={claim.id}
                    onClick={() => {
                      setSelectedClaimIndex(idx);
                      setSelectedEvidenceIndex(0);
                    }}
                    sx={{
                      textAlign: "left",
                      border: `1px solid ${
                        selected ? lensColor(claim.lens) : "rgba(0,0,0,0.16)"
                      }`,
                      borderRadius: 1,
                      bgcolor: selected ? lensBg(claim.lens) : "white",
                      color: "rgba(0,0,0,0.84)",
                      p: 0.85,
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: selected
                          ? lensBg(claim.lens)
                          : "rgba(0,0,0,0.025)",
                      },
                    }}
                  >
                    <Box sx={{ width: "100%", minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.65,
                          mb: 0.25,
                          minWidth: 0,
                        }}
                      >
                        {renderPlayerIdentity({
                          playerId: claim.playerId,
                          size: 30,
                          fontSize: 10,
                          noLabel: true,
                        })}
                        <Box
                          sx={{
                            fontSize: 14,
                            fontWeight: 900,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {claim.title}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          fontSize: 12.5,
                          color: "rgba(0,0,0,0.68)",
                          mb: 0.45,
                        }}
                      >
                        {claim.answer}
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {claim.evidence.map((ev, evIdx) => (
                          <Button
                            key={ev.id}
                            size="small"
                            variant="text"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedClaimIndex(idx);
                              setSelectedEvidenceIndex(evIdx);
                            }}
                            sx={{
                              minWidth: 34,
                              px: 0.75,
                              py: 0.28,
                              borderRadius: 0.75,
                              bgcolor: "rgba(0,0,0,0.055)",
                              fontSize: 12,
                              fontWeight: 900,
                              color: "rgba(0,0,0,0.58)",
                              lineHeight: 1.2,
                              textTransform: "none",
                            }}
                          >
                            R{ev.rallyIndex + 1}
                            {ev.shotNumber ? ` S${ev.shotNumber}` : ""}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ fontSize: 13, color: "rgba(0,0,0,0.62)" }}>
              No clear deterministic claims were found for p{selectedPlayerId}
              under this lens yet.
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              px: 1.15,
              py: 0.8,
              borderBottom: "1px solid rgba(0,0,0,0.1)",
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ fontSize: 18, fontWeight: 900 }}>
                {selectedClaim
                  ? `${activeLensInfo.label}: ${selectedClaim.title}`
                  : "No claim selected"}
              </Box>
              <Box sx={{ mt: 0.25, fontSize: 13, color: "rgba(0,0,0,0.68)" }}>
                {selectedClaim?.answer ?? "Pick a player and lens to start."}
              </Box>
              {selectedClaim ? (
                <Box sx={{ mt: 0.35, fontSize: 12.5, color: "rgba(0,0,0,0.58)" }}>
                  {selectedClaim.why}
                </Box>
              ) : null}
            </Box>
            <Box sx={{ display: "flex", gap: 0.45, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {selectedClaim?.evidence.map((ev, idx) => (
                <Button
                  key={ev.id}
                  size="small"
                  variant={idx === selectedEvidenceIndex ? "contained" : "outlined"}
                  onClick={() => setSelectedEvidenceIndex(idx)}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 900,
                    minWidth: 44,
                    px: 0.9,
                    py: 0.35,
                  }}
                >
                  R{ev.rallyIndex + 1}
                  {ev.shotNumber ? ` S${ev.shotNumber}` : ""}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, bgcolor: "black" }}>
            {selectedRow && selectedEvidence ? (
              <ActiveRallyVideo
                aiOpenViaParent={false}
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                renderPlayerIdentity={renderPlayerIdentity}
                aiContextLabel="Player Lens Review"
                focusedFilter={null}
                cvJson={cvJson}
                packageId={packageId}
                row={selectedRow}
                rows={rows}
                activeShotNumber={selectedEvidence.shotNumber}
                activeShotExtraText={activeLensInfo.label}
                shotOverlayPersistentExtraText={undefined}
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={true}
                showScoreEtc={true}
                showAi={true}
                aiDrawerMode="split"
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={true}
                isInFullPopup={false}
                autoPlayOnRallyChange={true}
                defaultPlaybackRate={selectedEvidence.shotNumber ? 0.25 : 1}
                seedPlayback={selectedSeedPlayback}
                seedLoopRegion={selectedLoopRegion}
                seekToActiveShotOnChange={false}
                focusedPlayerId={
                  selectedEvidence.pounceWindowRow?.focusedPlayerId ?? null
                }
                pounceOverlay={selectedPounceOverlay}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
